import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Input, Card } from '@/components';
import {
  validateEmail,
  validatePassword,
  validatePasswordConfirmation,
  validateCompanyName,
  checkPasswordRequirements,
} from '@/utils/validation';
import { supabase } from '@/lib/supabase';

interface SignUpFormData {
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface FormErrors {
  email: string;
  password: string;
  passwordConfirmation: string;
  submit: string;
}

export const SignUpPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Sign Up & Start Automating WhatsApp Print Orders | PrintDeck Direct';
  }, []);
  const [formData, setFormData] = useState<SignUpFormData>({
    email: '',
    password: '',
    passwordConfirmation: '',
  });

  const [errors, setErrors] = useState<FormErrors>({
    email: '',
    password: '',
    passwordConfirmation: '',
    submit: '',
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({
    email: false,
    password: false,
    passwordConfirmation: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const passwordRequirements = checkPasswordRequirements(formData.password);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear submit error when user starts typing
    if (errors.submit) {
      setErrors((prev) => ({ ...prev, submit: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate field on blur
    validateField(name);
  };

  const validateField = (fieldName: string) => {
    let error = '';

    switch (fieldName) {
      case 'email':
        error = validateEmail(formData.email) || '';
        break;
      case 'password':
        error = validatePassword(formData.password) || '';
        break;
      case 'passwordConfirmation':
        error =
          validatePasswordConfirmation(
            formData.password,
            formData.passwordConfirmation
          ) || '';
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return error === '';
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      email: validateEmail(formData.email) || '',
      password: validatePassword(formData.password) || '',
      passwordConfirmation:
        validatePasswordConfirmation(
          formData.password,
          formData.passwordConfirmation
        ) || '',
      submit: '',
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === '');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Call Supabase Auth signup (shop details will be collected in OnboardingPage)
      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (signUpError) throw signUpError;

      // Redirect to login with success message
      navigate('/login', {
        state: { message: 'Account created! Please sign in.' },
      });
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        'Failed to create account. Please try again.';

      setErrors((prev) => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = !Object.values(errors).some((error) => error !== '');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">
              Create Account
            </h1>
            <p className="text-gray-600 mt-2">
              Join PrintDeck and start automating your orders
            </p>
          </div>

          {/* Error Banner */}
          {errors.submit && (
            <div
              className="p-4 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
            >
              <p className="text-red-800 text-sm">✕ {errors.submit}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <Input
              id="email"
              label="Email Address"
              type="email"
              name="email"
              placeholder="owner@printnow.local"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email ? errors.email : ''}
              fullWidth
              disabled={isLoading}
              aria-invalid={touched.email && !!errors.email ? 'true' : 'false'}
              aria-describedby={
                touched.email && errors.email ? 'email-error' : undefined
              }
            />

            {/* Password */}
            <div>
              <Input
                id="password"
                label="Password"
                type="password"
                name="password"
                placeholder="Enter a strong password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => setShowPasswordRequirements(true)}
                error={touched.password ? errors.password : ''}
                fullWidth
                disabled={isLoading}
                aria-invalid={
                  touched.password && !!errors.password ? 'true' : 'false'
                }
                aria-describedby={
                  touched.password && errors.password
                    ? 'password-error'
                    : 'password-requirements'
                }
              />

              {/* Password Requirements */}
              {(showPasswordRequirements || formData.password) && (
                <div
                  id="password-requirements"
                  className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded space-y-2"
                >
                  <p className="text-xs font-semibold text-gray-700 uppercase">
                    Password Requirements:
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li
                      className={
                        passwordRequirements.hasMinLength
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }
                    >
                      {passwordRequirements.hasMinLength ? '✓' : '○'} At least 8
                      characters
                    </li>
                    <li
                      className={
                        passwordRequirements.hasUppercase
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }
                    >
                      {passwordRequirements.hasUppercase ? '✓' : '○'} One
                      uppercase letter (A-Z)
                    </li>
                    <li
                      className={
                        passwordRequirements.hasLowercase
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }
                    >
                      {passwordRequirements.hasLowercase ? '✓' : '○'} One
                      lowercase letter (a-z)
                    </li>
                    <li
                      className={
                        passwordRequirements.hasNumber
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }
                    >
                      {passwordRequirements.hasNumber ? '✓' : '○'} One number
                      (0-9)
                    </li>
                    <li
                      className={
                        passwordRequirements.hasSpecialChar
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }
                    >
                      {passwordRequirements.hasSpecialChar ? '✓' : '○'} One
                      special character (!@#$%^&*)
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <Input
              id="passwordConfirmation"
              label="Confirm Password"
              type="password"
              name="passwordConfirmation"
              placeholder="Confirm your password"
              value={formData.passwordConfirmation}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.passwordConfirmation ? errors.passwordConfirmation : ''}
              fullWidth
              disabled={isLoading}
              aria-invalid={
                touched.passwordConfirmation && !!errors.passwordConfirmation
                  ? 'true'
                  : 'false'
              }
              aria-describedby={
                touched.passwordConfirmation && errors.passwordConfirmation
                  ? 'passwordConfirmation-error'
                  : undefined
              }
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              isLoading={isLoading}
              disabled={isLoading || !isFormValid}
              aria-busy={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Footer */}
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                Sign in
              </Link>
            </p>
          </div>

          {/* Terms */}
          <p className="text-xs text-gray-500 text-center">
            By creating an account, you agree to our{' '}
            <a href="#" className="underline hover:text-gray-700">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-gray-700">
              Privacy Policy
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SignUpPage;
